import { FxButton, FxIcon } from '@/components/fx'
import { cn } from '@/lib/utils'
import { itemLabel, tooLargeNote, type CaptureItem } from './uploadQueue'

/**
 * One photographed page in the queue: a thumbnail, "Seite N · HH:MM" and the
 * item's own state — the artboard's 72px chip.
 *
 * The chip follows `FxFieldChip`'s review pair: `ground` normally,
 * `danger-subtle` when the item cannot go as it is. It is not that component
 * because that one is a form field, with a field-type glyph and a read value;
 * the only thing the two share is the shape.
 */
export function CaptureQueueItem({
  item,
  index,
  maxSizeMb,
  onRetry,
  onRemove,
}: {
  item: CaptureItem
  index: number
  maxSizeMb: number
  onRetry: (item: CaptureItem) => void
  onRemove: (item: CaptureItem) => void
}) {
  const failed = item.status === 'error' || item.status === 'tooLarge'
  const percent = Math.round(item.progress * 100)
  const label = itemLabel(index, item.takenAt)

  return (
    <li
      className={cn(
        'flex min-h-[72px] items-center gap-2 rounded-sm p-2',
        failed ? 'bg-danger-subtle' : 'bg-ground',
      )}
    >
      {item.previewUrl ? (
        <img
          src={item.previewUrl}
          alt=""
          className="h-14 w-11 shrink-0 bg-surface object-cover shadow-raised"
        />
      ) : (
        <span className="flex h-14 w-11 shrink-0 items-center justify-center bg-surface shadow-raised">
          <FxIcon name="description" size={22} className="text-text-muted" />
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body-strong">{label}</span>

        {item.status === 'ready' && <span className="text-small text-text-muted">bereit</span>}

        {item.status === 'sending' && (
          <>
            <span
              role="progressbar"
              aria-label={`${label} wird gesendet`}
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              className="mt-2 block h-1 rounded-sm bg-fill"
            >
              <span
                className="block h-1 rounded-sm bg-primary transition-[width]"
                style={{ width: `${percent}%` }}
              />
            </span>
            <span className="text-small text-text-muted">wird gesendet … {percent} %</span>
          </>
        )}

        {item.status === 'sent' && (
          <span className="inline-flex items-center gap-1 text-small text-success-text">
            <FxIcon name="check_circle" size={16} />
            gesendet
          </span>
        )}

        {item.status === 'error' && (
          <span className="text-small text-danger-text">{item.error ?? 'Fehler'}</span>
        )}

        {item.status === 'tooLarge' && (
          <span className="text-small text-danger-text">{tooLargeNote(maxSizeMb)}</span>
        )}
      </div>

      {item.status === 'error' && (
        <FxButton
          variant="dangerTonal"
          className="h-9 shrink-0 bg-surface px-3 text-small"
          onClick={() => onRetry(item)}
        >
          Erneut senden
        </FxButton>
      )}

      {/* The artboard draws no remove button; without one an oversized photo
          or a mis-shot page can never leave the queue, and "N Formulare
          senden" keeps counting a page nobody wants to send. 44px, because
          this is used one-handed. */}
      {item.status !== 'sending' && (
        <button
          type="button"
          onClick={() => onRemove(item)}
          aria-label={`${label} entfernen`}
          className="flex h-11 w-11 shrink-0 items-center justify-center text-text-muted"
        >
          <FxIcon name="close" size={20} />
        </button>
      )}
    </li>
  )
}
